import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { StatCard } from '../components/analytics/StatCard';
import { useToast } from '../components/ui/Toast';
import type {
  BillingOverview,
  RevenueTrendResponse,
  BillingPlan,
  BillingTransaction,
  BillingSettings,
  BillingSettingsPlan,
} from '../types';
import {
  DollarSign,
  TrendingUp,
  Users,
  CreditCard,
  CheckCircle2,
  Clock,
  XCircle,
  Download,
  Zap,
  Building2,
  Sparkles,
  RefreshCw,
  Save,
  Plus,
  Trash2,
  Search,
  X,
  Calendar,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { api } from '../services/api';
import { formatDateTime } from '../utils/helpers';

const CHART_TOOLTIP_STYLE = {
  contentStyle: {
    backgroundColor: '#141420',
    border: '1px solid #252535',
    borderRadius: '12px',
    fontSize: '13px',
  },
  labelStyle: { color: '#f1f5f9' },
  cursor: { fill: 'rgba(255,255,255,0.04)' },
};

/** Max rows returned for a transaction search (backend caps at 200). */
const TRANSACTION_SEARCH_LIMIT = 200;

function formatCurrency(amount: number, currency = 'INR') {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

/** "2026-09-10" -> "10 Sep 2026", read as a calendar date (no timezone shift). */
function formatDayLabel(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** e.g. `3 transactions for "acme" · 1 Sep 2026 – 28 Sep 2026 · showing first 200` */
function describeTransactionFilter(
  total: number,
  shown: number,
  query: string,
  from: string,
  to: string,
): string {
  const parts = [`${total} transaction${total !== 1 ? 's' : ''}`];
  if (query) parts[0] += ` for "${query}"`;
  if (from && to) parts.push(`${formatDayLabel(from)} – ${formatDayLabel(to)}`);
  else if (from) parts.push(`from ${formatDayLabel(from)}`);
  else if (to) parts.push(`up to ${formatDayLabel(to)}`);
  if (total > shown) parts.push(`showing first ${shown}`);
  return parts.join(' · ');
}

function escapeCsv(value: string | number | null | undefined): string {
  const normalized = value == null ? '' : String(value);
  if (!/[",\n]/.test(normalized)) return normalized;
  return `"${normalized.replace(/"/g, '""')}"`;
}

function StatusBadge({ status }: { status: 'paid' | 'pending' | 'failed' }) {
  const map = {
    paid: {
      variant: 'success' as const,
      icon: <CheckCircle2 className="w-3 h-3" />,
      label: 'Paid',
    },
    pending: {
      variant: 'warning' as const,
      icon: <Clock className="w-3 h-3" />,
      label: 'Pending',
    },
    failed: {
      variant: 'error' as const,
      icon: <XCircle className="w-3 h-3" />,
      label: 'Failed',
    },
  };

  const { variant, icon, label } = map[status];
  return (
    <Badge variant={variant} className="flex items-center gap-1">
      {icon}
      {label}
    </Badge>
  );
}

function getPlanVisual(planId: string, index: number) {
  const normalized = planId.toLowerCase();
  if (normalized.includes('starter')) {
    return {
      color: 'text-[#38bdf8]',
      iconBg: 'bg-[#38bdf8]/10',
      icon: <Sparkles className="w-4 h-4 text-[#38bdf8]" />,
    };
  }
  if (normalized.includes('basic')) {
    return {
      color: 'text-[#22c55e]',
      iconBg: 'bg-[#22c55e]/10',
      icon: <Zap className="w-4 h-4 text-[#22c55e]" />,
    };
  }
  if (normalized.includes('pro')) {
    return {
      color: 'text-[#a855f7]',
      iconBg: 'bg-[#a855f7]/10',
      icon: <Building2 className="w-4 h-4 text-[#a855f7]" />,
    };
  }

  const fallbacks = [
    {
      color: 'text-[#3b82f6]',
      iconBg: 'bg-[#3b82f6]/10',
      icon: <Sparkles className="w-4 h-4 text-[#3b82f6]" />,
    },
    {
      color: 'text-[#22c55e]',
      iconBg: 'bg-[#22c55e]/10',
      icon: <Zap className="w-4 h-4 text-[#22c55e]" />,
    },
    {
      color: 'text-[#a855f7]',
      iconBg: 'bg-[#a855f7]/10',
      icon: <Building2 className="w-4 h-4 text-[#a855f7]" />,
    },
  ];
  return fallbacks[index % fallbacks.length];
}

function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-1.5">
      {children}
    </label>
  );
}

export function Billing() {
  const { toast } = useToast();
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [defaultRevenue, setDefaultRevenue] = useState<RevenueTrendResponse | null>(null);
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [recentTransactions, setRecentTransactions] = useState<BillingTransaction[]>([]);

  // Revenue Over Time: optional custom period. Empty = the default last 6 months.
  const [revFrom, setRevFrom] = useState('');
  const [revTo, setRevTo] = useState('');
  const [rangeRevenue, setRangeRevenue] = useState<{ key: string; data: RevenueTrendResponse | null } | null>(null);
  const revKey = `${revFrom}|${revTo}`;
  const revRangeActive = Boolean(revFrom || revTo);
  const revenue = revRangeActive
    ? (rangeRevenue?.key === revKey ? rangeRevenue.data : null)
    : defaultRevenue;
  const revenueLoading = revRangeActive && rangeRevenue?.key !== revKey;

  // Recent Transactions search + date range (text is debounced; both run
  // server-side across all purchases, not just the rows loaded on the page).
  const [txSearch, setTxSearch] = useState('');
  const [txQuery, setTxQuery] = useState('');
  const [txFrom, setTxFrom] = useState('');
  const [txTo, setTxTo] = useState('');
  const [searchResult, setSearchResult] = useState<
    { key: string; transactions: BillingTransaction[]; total: number } | null
  >(null);
  const txKey = `${txQuery}|${txFrom}|${txTo}`;
  const searching = Boolean(txQuery || txFrom || txTo);
  const searchResultCurrent = searchResult?.key === txKey ? searchResult : null;
  const transactions = useMemo(
    () => (searching ? (searchResultCurrent?.transactions ?? []) : recentTransactions),
    [searching, searchResultCurrent, recentTransactions],
  );
  const searchLoading = (searching && !searchResultCurrent) || txSearch.trim() !== txQuery;
  const [billingSettings, setBillingSettings] = useState<BillingSettings | null>(null);
  const [initialSettingsSnapshot, setInitialSettingsSnapshot] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchBillingData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      setError(null);
      const [overviewData, revenueData, plansData, transactionsData, settingsData] = await Promise.all([
        api.getBillingOverview(),
        api.getBillingRevenueTrend(6),
        api.getBillingPlans(),
        api.getBillingTransactions(30),
        api.getBillingSettings(),
      ]);

      setOverview(overviewData);
      setDefaultRevenue(revenueData);
      setPlans(plansData.plans);
      setRecentTransactions(transactionsData.transactions);
      setBillingSettings(settingsData);
      setInitialSettingsSnapshot(JSON.stringify(settingsData));
      setLastUpdated(new Date());
    } catch (err: any) {
      const message = err?.message || 'Failed to load billing data';
      setError(message);
      if (isRefresh) {
        toast(message, 'error');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchBillingData();
  }, [fetchBillingData]);

  useEffect(() => {
    if (!revFrom && !revTo) return;
    let cancelled = false;
    const key = `${revFrom}|${revTo}`;
    api.getBillingRevenueTrend(6, { createdFrom: revFrom || undefined, createdTo: revTo || undefined })
      .then((res) => { if (!cancelled) setRangeRevenue({ key, data: res }); })
      .catch((err) => {
        if (cancelled) return;
        setRangeRevenue({ key, data: null });
        toast(err instanceof Error ? err.message : 'Failed to load revenue for this period.', 'error');
      });
    return () => { cancelled = true; };
    // lastUpdated: the page's Refresh button re-runs the period query too.
  }, [revFrom, revTo, lastUpdated, toast]);

  useEffect(() => {
    const timer = setTimeout(() => setTxQuery(txSearch.trim()), 300);
    return () => clearTimeout(timer);
  }, [txSearch]);

  useEffect(() => {
    if (!txQuery && !txFrom && !txTo) return;
    let cancelled = false;
    const key = `${txQuery}|${txFrom}|${txTo}`;
    api.getBillingTransactions(TRANSACTION_SEARCH_LIMIT, {
      search: txQuery || undefined,
      createdFrom: txFrom || undefined,
      createdTo: txTo || undefined,
    })
      .then((res) => {
        if (!cancelled) setSearchResult({ key, transactions: res.transactions, total: res.total });
      })
      .catch((err) => {
        if (cancelled) return;
        setSearchResult({ key, transactions: [], total: 0 });
        toast(err instanceof Error ? err.message : 'Transaction search failed.', 'error');
      });
    return () => { cancelled = true; };
  }, [txQuery, txFrom, txTo, lastUpdated, toast]);

  const exportCsv = useCallback(() => {
    if (!transactions.length) {
      toast('No transactions to export yet.', 'info');
      return;
    }

    const headers = [
      'Transaction ID',
      'User Name',
      'User Email',
      'Plan',
      'Amount',
      'Currency',
      'Credits',
      'Status',
      'Payment ID',
      'Date',
    ];

    const rows = transactions.map((tx) => [
      tx.id,
      tx.userName,
      tx.userEmail,
      tx.plan,
      tx.amount,
      tx.currency,
      tx.credits,
      tx.status,
      tx.paymentId ?? '',
      tx.createdAt ?? '',
    ]);

    const csv = [
      headers.map(escapeCsv).join(','),
      ...rows.map((row) => row.map(escapeCsv).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `billing-transactions-${new Date().toISOString().slice(0, 10)}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast('Transactions exported successfully.', 'success');
  }, [toast, transactions]);

  const updateBillingField = useCallback(
    <K extends keyof BillingSettings>(key: K, value: BillingSettings[K]) => {
      setBillingSettings((prev) => (prev ? { ...prev, [key]: value } : prev));
    },
    []
  );

  const updatePackage = useCallback((index: number, patch: Partial<BillingSettingsPlan>) => {
    setBillingSettings((prev) => {
      if (!prev) return prev;
      const packages = prev.packages.map((pkg, pkgIndex) =>
        pkgIndex === index ? { ...pkg, ...patch } : pkg
      );
      return { ...prev, packages };
    });
  }, []);

  const addPackage = useCallback(() => {
    setBillingSettings((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        packages: [
          ...prev.packages,
          {
            name: '',
            credits: 10,
            amount: 9900,
            currency: prev.currency,
            description: '',
            popular: false,
          },
        ],
      };
    });
  }, []);

  const removePackage = useCallback((index: number) => {
    setBillingSettings((prev) => {
      if (!prev || prev.packages.length <= 1) return prev;
      return {
        ...prev,
        packages: prev.packages.filter((_, pkgIndex) => pkgIndex !== index),
      };
    });
  }, []);

  const handleSaveSettings = useCallback(async () => {
    if (!billingSettings) return;

    const hasInvalidPackage = billingSettings.packages.some(
      (pkg) =>
        !pkg.name.trim() ||
        !pkg.description.trim() ||
        Number(pkg.credits) <= 0 ||
        Number(pkg.amount) <= 0
    );

    if (billingSettings.initial_credits < 0) {
      toast('Initial credits cannot be negative.', 'error');
      return;
    }

    if (billingSettings.recruiter_unlock_cost <= 0) {
      toast('Unlock cost must be at least 1 credit.', 'error');
      return;
    }

    if (billingSettings.packages.length === 0 || hasInvalidPackage) {
      toast('Every package needs a name, description, positive price, and positive credits.', 'error');
      return;
    }

    try {
      setSavingSettings(true);
      const payload: BillingSettings = {
        ...billingSettings,
        packages: billingSettings.packages.map((pkg) => ({
          ...pkg,
          name: pkg.name.trim(),
          description: pkg.description.trim(),
          currency: billingSettings.currency,
        })),
      };
      const saved = await api.updateBillingSettings(payload);
      setBillingSettings(saved);
      setInitialSettingsSnapshot(JSON.stringify(saved));
      await fetchBillingData(true);
      toast('Billing settings updated successfully.', 'success');
    } catch (err: any) {
      toast(err?.message || 'Failed to update billing settings.', 'error');
    } finally {
      setSavingSettings(false);
    }
  }, [billingSettings, fetchBillingData, toast]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin w-8 h-8 border-2 border-[#3b82f6] border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-12 h-12 bg-red-500/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <span className="text-[#ef4444] text-xl">!</span>
            </div>
            <p className="text-[#ef4444] font-medium">Failed to load billing</p>
            <p className="text-[#64748b] text-sm mt-1">{error}</p>
            <button
              onClick={() => fetchBillingData()}
              className="mt-4 px-4 py-2 text-sm rounded-xl bg-[#141420] border border-[#252535] text-[#94a3b8] hover:text-[#f1f5f9] hover:border-[#353548] transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const mrr = Math.round(overview?.mrr ?? 0);
  const arr = Math.round(overview?.arr ?? 0);
  const totalSubscribers = overview?.totalSubscribers ?? 0;
  const churnRate = Number((overview?.churnRate ?? 0).toFixed(2));
  const currency = overview?.currency ?? 'INR';
  const settingsDirty =
    billingSettings != null && JSON.stringify(billingSettings) !== initialSettingsSnapshot;

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-[#f1f5f9]">Billing</h2>
            <p className="text-sm text-[#64748b] mt-1">
              Revenue performance, package analytics, and payment history
            </p>
          </div>
          <div className="flex items-center gap-2">
            {lastUpdated && (
              <span className="text-xs text-[#64748b] hidden sm:inline">
                Updated {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={() => fetchBillingData(true)}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#141420] border border-[#252535] text-sm text-[#94a3b8] hover:text-[#f1f5f9] hover:border-[#353548] transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCard
            title={`Monthly Revenue (${currency})`}
            value={mrr}
            icon={<DollarSign className="w-5 h-5" />}
            accent="green"
          />
          <StatCard
            title={`Annual Revenue (${currency})`}
            value={arr}
            icon={<TrendingUp className="w-5 h-5" />}
            accent="blue"
          />
          <StatCard
            title="Paying Subscribers"
            value={totalSubscribers}
            icon={<Users className="w-5 h-5" />}
            accent="purple"
          />
          <StatCard
            title="Churn Rate (%)"
            value={churnRate}
            icon={<CreditCard className="w-5 h-5" />}
            accent="red"
          />
        </div>

        {billingSettings && (
          <Card>
            <CardHeader
              title="Billing Controls"
              subtitle="Change signup credits, recruiter unlock cost, and recharge packages"
              action={
                <Button
                  variant="primary"
                  onClick={handleSaveSettings}
                  loading={savingSettings}
                  disabled={!settingsDirty}
                  icon={<Save className="w-4 h-4" />}
                >
                  Save Changes
                </Button>
              }
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <FieldLabel>Initial Credits</FieldLabel>
                <input
                  type="number"
                  min={0}
                  value={billingSettings.initial_credits}
                  onChange={(e) =>
                    updateBillingField('initial_credits', Math.max(0, Number(e.target.value) || 0))
                  }
                  className="w-full px-3.5 py-2.5 bg-[#1c1c2a] border border-[#252535] rounded-xl text-sm text-[#f1f5f9] focus:outline-none focus:border-[#3b82f6]"
                />
                <p className="text-xs text-[#64748b] mt-1.5">Free credits given to a new recruiter account.</p>
              </div>

              <div>
                <FieldLabel>Recruiter Chat Unlock Cost</FieldLabel>
                <input
                  type="number"
                  min={1}
                  value={billingSettings.recruiter_unlock_cost}
                  onChange={(e) =>
                    updateBillingField(
                      'recruiter_unlock_cost',
                      Math.max(1, Number(e.target.value) || 1)
                    )
                  }
                  className="w-full px-3.5 py-2.5 bg-[#1c1c2a] border border-[#252535] rounded-xl text-sm text-[#f1f5f9] focus:outline-none focus:border-[#3b82f6]"
                />
                <p className="text-xs text-[#64748b] mt-1.5">Credits deducted when a recruiter unlocks a candidate chat.</p>
              </div>

              <div>
                <FieldLabel>Currency</FieldLabel>
                <input
                  type="text"
                  value={billingSettings.currency}
                  onChange={(e) => updateBillingField('currency', e.target.value.toUpperCase() || 'INR')}
                  className="w-full px-3.5 py-2.5 bg-[#1c1c2a] border border-[#252535] rounded-xl text-sm text-[#f1f5f9] focus:outline-none focus:border-[#3b82f6]"
                />
                <p className="text-xs text-[#64748b] mt-1.5">Applied to all billing plans and analytics displays.</p>
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between">
              <div>
                <h4 className="text-sm font-semibold text-[#f1f5f9]">Recharge Plans</h4>
                <p className="text-sm text-[#64748b] mt-0.5">Control how much users pay and how many credits they receive.</p>
              </div>
              <Button variant="secondary" onClick={addPackage} icon={<Plus className="w-4 h-4" />}>
                Add Plan
              </Button>
            </div>

            <div className="mt-5 space-y-4">
              {billingSettings.packages.map((plan, index) => (
                <div key={plan.id ?? `draft-${index}`} className="rounded-2xl border border-[#252535] bg-[#10101a] p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-[#f1f5f9]">
                        Plan {index + 1}
                        {plan.id ? ` · ${plan.id}` : ' · ID generated on save'}
                      </p>
                      <p className="text-xs text-[#64748b] mt-1">
                        Existing transaction analytics stay mapped by plan id where possible.
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      onClick={() => removePackage(index)}
                      disabled={billingSettings.packages.length <= 1}
                      icon={<Trash2 className="w-4 h-4" />}
                    >
                      Remove
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                    <div>
                      <FieldLabel>Name</FieldLabel>
                      <input
                        type="text"
                        value={plan.name}
                        onChange={(e) => updatePackage(index, { name: e.target.value })}
                        className="w-full px-3.5 py-2.5 bg-[#1c1c2a] border border-[#252535] rounded-xl text-sm text-[#f1f5f9] focus:outline-none focus:border-[#3b82f6]"
                      />
                    </div>

                    <div>
                      <FieldLabel>Credits</FieldLabel>
                      <input
                        type="number"
                        min={1}
                        value={plan.credits}
                        onChange={(e) =>
                          updatePackage(index, { credits: Math.max(1, Number(e.target.value) || 1) })
                        }
                        className="w-full px-3.5 py-2.5 bg-[#1c1c2a] border border-[#252535] rounded-xl text-sm text-[#f1f5f9] focus:outline-none focus:border-[#3b82f6]"
                      />
                    </div>

                    <div>
                      <FieldLabel>Price ({billingSettings.currency})</FieldLabel>
                      <input
                        type="number"
                        min={1}
                        value={plan.amount / 100}
                        onChange={(e) =>
                          updatePackage(index, {
                            amount: Math.max(1, Math.round((Number(e.target.value) || 0) * 100)),
                          })
                        }
                        className="w-full px-3.5 py-2.5 bg-[#1c1c2a] border border-[#252535] rounded-xl text-sm text-[#f1f5f9] focus:outline-none focus:border-[#3b82f6]"
                      />
                    </div>

                    <div>
                      <FieldLabel>Popular Badge</FieldLabel>
                      <button
                        onClick={() => updatePackage(index, { popular: !plan.popular })}
                        className={`w-full px-3.5 py-2.5 rounded-xl text-sm border transition-colors ${
                          plan.popular
                            ? 'bg-[#3b82f6]/15 border-[#3b82f6] text-[#93c5fd]'
                            : 'bg-[#1c1c2a] border-[#252535] text-[#94a3b8]'
                        }`}
                      >
                        {plan.popular ? 'Marked as popular' : 'Mark as popular'}
                      </button>
                    </div>
                  </div>

                  <div className="mt-4">
                    <FieldLabel>Description</FieldLabel>
                    <textarea
                      rows={3}
                      value={plan.description}
                      onChange={(e) => updatePackage(index, { description: e.target.value })}
                      className="w-full px-3.5 py-2.5 bg-[#1c1c2a] border border-[#252535] rounded-xl text-sm text-[#f1f5f9] placeholder-[#64748b] focus:outline-none focus:border-[#3b82f6]"
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <Card>
          <CardHeader
            title="Revenue Over Time"
            subtitle={
              revRangeActive && revenue?.from && revenue.to
                ? `${formatDayLabel(revenue.from)} – ${formatDayLabel(revenue.to)} · ${
                    revenue.granularity === 'day' ? 'daily' : 'monthly'
                  }`
                : 'Last 6 months revenue trend'
            }
          />

          <div className="flex flex-wrap items-end gap-3 mb-5">
            <div className="flex items-center gap-2 text-xs font-semibold text-[#94a3b8] uppercase tracking-wider shrink-0 pb-2.5">
              <Calendar className="w-4 h-4" />
              <span>Period</span>
            </div>
            <div className="w-[170px]">
              <FieldLabel>From</FieldLabel>
              <input
                type="date"
                value={revFrom}
                max={revTo || undefined}
                onChange={(e) => setRevFrom(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-[#1c1c2a] border border-[#252535] rounded-xl text-sm text-[#f1f5f9] focus:outline-none focus:border-[#3b82f6] [color-scheme:dark]"
              />
            </div>
            <div className="w-[170px]">
              <FieldLabel>To</FieldLabel>
              <input
                type="date"
                value={revTo}
                min={revFrom || undefined}
                onChange={(e) => setRevTo(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-[#1c1c2a] border border-[#252535] rounded-xl text-sm text-[#f1f5f9] focus:outline-none focus:border-[#3b82f6] [color-scheme:dark]"
              />
            </div>
            {revRangeActive && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setRevFrom(''); setRevTo(''); }}
                icon={<X className="w-4 h-4" />}
              >
                Clear
              </Button>
            )}
            {revenueLoading && (
              <div className="pb-2.5">
                <div className="animate-spin w-4 h-4 border-2 border-[#3b82f6] border-t-transparent rounded-full" />
              </div>
            )}
          </div>

          {revenue?.summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {[
                { label: 'Revenue', value: formatCurrency(revenue.summary.revenue, revenue.currency), accent: 'text-[#22c55e]' },
                { label: 'Purchases', value: revenue.summary.transactions, accent: 'text-[#f1f5f9]' },
                { label: 'Credits Sold', value: revenue.summary.creditsSold, accent: 'text-[#f1f5f9]' },
                { label: 'Paying Users', value: revenue.summary.payingUsers, accent: 'text-[#f1f5f9]' },
              ].map((s) => (
                <div key={s.label} className="rounded-xl border border-[#252535] bg-[#10101a] px-4 py-3">
                  <p className="text-[10px] font-semibold text-[#64748b] uppercase tracking-wider">{s.label}</p>
                  <p className={`text-xl font-bold mt-1 ${s.accent}`}>{s.value}</p>
                </div>
              ))}
            </div>
          )}

          {revenueLoading ? (
            <div className="h-[240px] animate-shimmer rounded-xl" />
          ) : !revenue?.data?.length || revenue.summary?.transactions === 0 ? (
            <div className="h-[240px] flex items-center justify-center text-sm text-[#64748b]">
              {revRangeActive ? 'No revenue in this period' : 'No revenue data available'}
            </div>
          ) : (
            <div className="h-[240px] mt-2">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenue.data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#252535" vertical={false} />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis
                    stroke="#64748b"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => formatCurrency(Number(value), currency)}
                  />
                  <Tooltip
                    {...CHART_TOOLTIP_STYLE}
                    formatter={(value) => [formatCurrency(Number(value), currency), 'Revenue']}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#22c55e"
                    strokeWidth={2}
                    fill="url(#revenueGrad)"
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <div>
          <div className="mb-5">
            <h3 className="text-base font-semibold text-[#f1f5f9]">Credit Packages</h3>
            <p className="text-sm text-[#64748b] mt-0.5">Package performance across all customers</p>
          </div>
          {plans.length === 0 ? (
            <Card>
              <div className="py-10 text-sm text-[#64748b] text-center">No package data available</div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {plans.map((plan, index) => {
                const visual = getPlanVisual(plan.id, index);
                return (
                  <div
                    key={plan.id}
                    className="relative bg-[#141420] border border-[#252535] rounded-2xl p-6 flex flex-col gap-4"
                  >
                    {plan.popular && (
                      <span className="absolute top-4 right-4">
                        <Badge variant="info">Most Popular</Badge>
                      </span>
                    )}

                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl ${visual.iconBg} flex items-center justify-center`}>
                        {visual.icon}
                      </div>
                      <div>
                        <p className={`font-semibold text-sm ${visual.color}`}>{plan.name}</p>
                        <p className="text-xs text-[#64748b]">{plan.credits} credits</p>
                      </div>
                    </div>

                    <div className="text-3xl font-bold text-[#f1f5f9]">
                      {formatCurrency(plan.price, plan.currency)}
                    </div>

                    <p className="text-sm text-[#94a3b8] min-h-[40px]">{plan.description}</p>

                    <div className="mt-auto pt-4 border-t border-[#252535] space-y-2 text-xs">
                      <div className="flex items-center justify-between text-[#64748b]">
                        <span>Subscribers</span>
                        <span className="font-semibold text-[#94a3b8]">{plan.subscribers}</span>
                      </div>
                      <div className="flex items-center justify-between text-[#64748b]">
                        <span>Purchases</span>
                        <span className="font-semibold text-[#94a3b8]">{plan.transactions}</span>
                      </div>
                      <div className="flex items-center justify-between text-[#64748b]">
                        <span>Credits Sold</span>
                        <span className="font-semibold text-[#94a3b8]">{plan.creditsSold}</span>
                      </div>
                      <div className="flex items-center justify-between text-[#64748b]">
                        <span>Revenue</span>
                        <span className="font-semibold text-[#22c55e]">
                          {formatCurrency(plan.revenue, plan.currency)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <Card padding="none">
          <div className="p-6 border-b border-[#252535] flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-base font-semibold text-[#f1f5f9]">Recent Transactions</h3>
              <p className="text-sm text-[#64748b] mt-0.5">
                {searching && searchResultCurrent
                  ? describeTransactionFilter(searchResultCurrent.total, transactions.length, txQuery, txFrom, txTo)
                  : 'Latest customer payment transactions'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative w-full md:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                <input
                  type="search"
                  value={txSearch}
                  onChange={(e) => setTxSearch(e.target.value)}
                  placeholder="Search name, email, plan, payment ID..."
                  aria-label="Search transactions"
                  className="w-full pl-9 pr-9 py-2 bg-[#1c1c2a] border border-[#252535] rounded-xl text-sm text-[#f1f5f9] placeholder-[#64748b] focus:outline-none focus:border-[#3b82f6]"
                />
                {searchLoading && txSearch.trim() ? (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin w-3.5 h-3.5 border-2 border-[#3b82f6] border-t-transparent rounded-full" />
                ) : null}
              </div>
              <button
                onClick={exportCsv}
                disabled={!transactions.length}
                className="flex shrink-0 items-center gap-2 px-3 py-2 rounded-xl bg-[#1c1c2a] border border-[#252535] text-sm text-[#94a3b8] hover:text-[#f1f5f9] hover:border-[#3a3a4a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-3.5 h-3.5" />
                Export CSV
              </button>
            </div>
          </div>

          <div className="px-6 py-4 border-b border-[#252535] flex flex-wrap items-end gap-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-[#94a3b8] uppercase tracking-wider shrink-0 pb-2.5">
              <Calendar className="w-4 h-4" />
              <span>Purchased</span>
            </div>
            <div className="w-[170px]">
              <FieldLabel>From</FieldLabel>
              <input
                type="date"
                value={txFrom}
                max={txTo || undefined}
                onChange={(e) => setTxFrom(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-[#1c1c2a] border border-[#252535] rounded-xl text-sm text-[#f1f5f9] focus:outline-none focus:border-[#3b82f6] [color-scheme:dark]"
              />
            </div>
            <div className="w-[170px]">
              <FieldLabel>To</FieldLabel>
              <input
                type="date"
                value={txTo}
                min={txFrom || undefined}
                onChange={(e) => setTxTo(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-[#1c1c2a] border border-[#252535] rounded-xl text-sm text-[#f1f5f9] focus:outline-none focus:border-[#3b82f6] [color-scheme:dark]"
              />
            </div>
            {(txSearch || txFrom || txTo) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setTxSearch(''); setTxQuery(''); setTxFrom(''); setTxTo(''); }}
                icon={<X className="w-4 h-4" />}
              >
                Clear filters
              </Button>
            )}
          </div>

          {searching && searchLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin w-6 h-6 border-2 border-[#3b82f6] border-t-transparent rounded-full" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-12 h-12 bg-[#1c1c2a] rounded-2xl flex items-center justify-center mb-3">
                <CreditCard className="w-5 h-5 text-[#64748b]" />
              </div>
              <p className="text-sm font-medium text-[#94a3b8]">
                {searching ? 'No transactions match these filters' : 'No transactions yet'}
              </p>
              <p className="text-xs text-[#64748b] mt-1">
                {searching
                  ? 'Try a different search or widen the dates'
                  : 'Transactions will appear once customers start purchasing credits'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#252535]">
                    {['User', 'Plan', 'Amount', 'Credits', 'Status', 'Date', 'Reference'].map((header) => (
                      <th
                        key={header}
                        className="text-left px-6 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wider"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr key={tx.id} className="border-b border-[#252535] hover:bg-[#1c1c2a] transition-colors">
                      <td className="px-6 py-4">
                        <p className="font-medium text-[#f1f5f9]">{tx.userName}</p>
                        <p className="text-xs text-[#64748b]">{tx.userEmail}</p>
                      </td>
                      <td className="px-6 py-4 text-[#94a3b8]">{tx.plan}</td>
                      <td className="px-6 py-4 font-semibold text-[#f1f5f9]">
                        {formatCurrency(tx.amount, tx.currency)}
                      </td>
                      <td className="px-6 py-4 text-[#94a3b8]">{tx.credits}</td>
                      <td className="px-6 py-4">
                        <StatusBadge status={tx.status} />
                      </td>
                      <td className="px-6 py-4 text-[#64748b]">{formatDateTime(tx.createdAt ?? undefined)}</td>
                      <td className="px-6 py-4 text-xs text-[#3b82f6]">
                        {tx.paymentId || tx.invoiceRef || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
